import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import { useDrag } from 'react-use-gesture';
import { animated, useSpring } from '@react-spring/web';
import './style.less';
import { Icon } from 'shared/components/Icon';
import { Button } from 'shared/components/Button';
import { ConnectedCallDetail } from '../Outbound/ConnectedCallDetail';
import { useOutbound } from '../Outbound/useOutbound';
import { AppState } from 'shared/store/reducers';
import { useDispatch, useSelector } from 'react-redux';
import { setDetailOpen } from 'shared/store/outbound-slice';
import { OutboundSessionStates } from '../Outbound/OutboundSession';
import { useOutboundContext } from '../Outbound/outbound-provider';
import { formatDuration } from 'shared/utils/helpers';
import { DispositionModal } from '../Outbound/ConnectedCallDetail/DispositionModal';
import { CallTaskStatus, CompletedCallTaskStates } from 'types/outbound';

export const OutboundActiveCall: React.FC = () => {
  const {
    callNextNumber,
    isConnected,
    isDialing,
    isOpen,
    markNumberInvalid,
    skipCall,
    startDialpadDialing,
    startSingleTaskDialing,
    stopCall,
  } = useOutbound();
  const { session } = useOutboundContext();
  const [dispositionModalVisible, setDispositionModalVisible] = useState(false);
  const { status, detailTask, detailOpen } = useSelector(
    (state: AppState) => state.outbound,
  );
  const isVisible = useMemo(() => {
    return status === OutboundSessionStates.connected;
  }, [status]);

  const [duration, setDuration] = useState<string | null>(null);
  const durationInterval = useRef<NodeJS.Timeout | null>(null);
  const [shouldRender, setShouldRender] = useState(isVisible);

  useEffect(() => {
    if (isVisible) setShouldRender(true);
  }, [isVisible]);

  const fadeSpring = useSpring({
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? 'scale(1)' : 'scale(0.95)',
    config: { tension: 300, friction: 20 },
    onRest: () => {
      if (!isVisible) setShouldRender(false);
    },
  });

  const dispatch = useDispatch();

  const { pathname } = useLocation();

  const contact = detailTask?.contact;

  const name = contact?.name || 'N/A';
  const title = `${contact?.title || contact?.customMetadata?.title || 'Unknown Title'}`;
  const company = `${contact?.company?.name || 'Unknown Company'}`;
  const location = contact?.company?.location || 'Unknown Location';

  const [{ x, y }, api] = useSpring(() => ({ x: 0, y: 0 }));
  const bind = useDrag(
    ({ down, offset: [ox, oy] }) => api.start({ x: ox, y: oy, immediate: down }),
    {
      bounds: {
        left: 0,
        bottom: 0,
        right: window.innerWidth - 568,
        top:
          (window.innerHeight -
            64 -
            (document.querySelector('.OutboundActiveCall')?.clientHeight ?? 168)) *
          -1,
      },
    },
  );

  const springProps = useSpring({ x, y });

  useEffect(() => {
    if (!session.current.callStartedAt) {
      if (!durationInterval.current) return;
      clearInterval(durationInterval.current);

      return;
    }
    durationInterval.current = setInterval(() => {
      const now = new Date();
      const differenceInSeconds = Math.floor(
        /* @ts-ignore */
        (now - session.current.callStartedAt) / 1000,
      );

      setDuration(formatDuration(differenceInSeconds * 1000));
    }, 1000);

    return () => {
      if (!durationInterval.current) return;
      clearInterval(durationInterval.current);
    };
  }, [session, session.current.callStartedAt]);

  useEffect(() => {
    if (
      !CompletedCallTaskStates.includes(detailTask?.status as CallTaskStatus) ||
      detailOpen ||
      ['/outbound', '/salesfloor'].some(p => pathname.startsWith(p))
    ) {
      return;
    }

    setDispositionModalVisible(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailTask]);

  if (['/outbound', '/salesfloor'].some(p => pathname.startsWith(p))) {
    return null;
  }

  return (
    <>
      {shouldRender && (
        <animated.div
          className={'OutboundActiveCall'}
          style={{
            ...springProps,
            ...fadeSpring,
            x: springProps.x.to(x => `${x}px`),
            y: springProps.y.to(y => `${y}px`),
          }}
        >
          <div className="OutboundActiveCall__drag" {...bind()}>
            <Icon width={14} height={14} icon="drag" />
          </div>
          <div className="OutboundActiveCall__iconWrap">
            <div className="OutboundActiveCall__icon">
              <Icon icon="callInbound" width={20} height={20} />
            </div>
          </div>
          <div className="OutboundActiveCall__wrap">
            <div className="OutboundActiveCall__content">
              <div className="OutboundActiveCall__contactWrap">
                <div className="OutboundActiveCall__contact">
                  {name}
                  <small>{title}</small>
                </div>
                <div className="OutboundActiveCall__pills">
                  <div className="OutboundActiveCall__pill">
                    <Icon icon="phoneOutline" width={12} height={12} />
                    Call in Progress
                  </div>
                  {!!duration && (
                    <div className="OutboundActiveCall__pill OutboundActiveCall__pill--duration">
                      {duration}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="OutboundActiveCall__contentWrap">
              <div className="OutboundActiveCall__contentItem">
                <Icon icon="companies2" />
                {company}
              </div>
              <div className="OutboundActiveCall__contentItem">
                <Icon icon="pinOutline" />
                {location}
              </div>
            </div>
            <div className="OutboundActiveCall__actions">
              <Button
                type="primary"
                size="small"
                className="OutboundActiveCall__button"
                onClick={() => stopCall()}
              >
                <Icon icon="phoneOff" width={14} height={14} />
                End Call
              </Button>
              <Button
                type="ghost"
                size="small"
                className="OutboundActiveCall__button"
                to="/outbound"
              >
                <Icon icon="dialer" width={14} height={14} />
                Go to Dialer
              </Button>
              <Button
                type="ghost"
                size="small"
                className="OutboundActiveCall__button"
                onClick={() => dispatch(setDetailOpen(true))}
              >
                <Icon icon="userOutline" width={14} height={14} />
                Open Details
              </Button>
            </div>
          </div>
        </animated.div>
      )}
      <ConnectedCallDetail
        isConnected={isConnected}
        isDialing={isDialing}
        isOpen={isOpen}
        onCallNextClick={callNextNumber}
        onDial={startSingleTaskDialing}
        onDialWithoutTask={startDialpadDialing}
        onInvalidNumberClick={markNumberInvalid}
        onSkip={skipCall}
        onStop={stopCall}
      />
      <DispositionModal
        visible={dispositionModalVisible}
        close={() => setDispositionModalVisible(false)}
        setVisible={() => {
          setDispositionModalVisible(false);
        }}
      />
    </>
  );
};
